import CN from "classnames";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/Button";
import ImageUpload, { Image } from "./plugins/ImageUpload";
import { useSelector } from "react-redux";
import store from "../../store/store";
import { Oval } from "react-loader-spinner";
import { editBrand } from "./reducer/brandDetailsSlice";

interface FormValues {
  name: string;
  website: string;
  country: string;
  contact: string;
  file?: File[] | string;
}

export const EditBrand: React.FC<any> = () => {
  const { brandDetails, isLoading, isSuccess } = useSelector(
    (state: any) => state.rootReducer?.brandDetail
  );
  const navigate = useNavigate();

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Brand Name is required")
      .max(50, "Brand Name must be 50 characters or less"),
    website: Yup.string(),
        country: Yup.string(),
        contact: Yup.string(),
    // images: Yup.array().min(1, "At least one image is required"),
    // logo: Yup.array(),
  });

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    values,
    errors,
    touched,
    dirty,
  } = useFormik<FormValues>({
    initialValues: {
      name: brandDetails?.name,
      website: brandDetails?.website,
      country: brandDetails?.country,
      contact: brandDetails?.contact,
      file: brandDetails?.logo,
    },
    validationSchema,
    onSubmit: (values: FormValues) => {
      const formData = new FormData();
      console.log("values", values);

      const convertToFile = async (image: {
        path: string;
        public_id: string;
      }): Promise<File> => {
        const fileName = image.path.split("/").pop() || "uploaded_file";
        const response = await fetch(image.path);
        const blob = await response.blob();
        return new File([blob], fileName, { type: blob.type });
      };

      (async () => {
        for (const [key, value] of Object.entries(values)) {
          if (key === "file") {
            // Process the single image (File or existing image object)
            if (value) {
              if (value instanceof File) {
                // New image, add directly
                formData.append("file", value);
              } else {
                // Existing image, convert to File
                const file = await convertToFile(
                  value as { path: string; public_id: string }
                );
                formData.append("file", file);
              }
            }
          } else if (Array.isArray(value)) {
            // Handle other array fields
            value.forEach((val) => formData.append(`${key}[]`, val));
          } else {
            // Handle other fields
            formData.append(key, value as string);
          }
        }

        // Dispatch the action with the formData
        store.dispatch(
          editBrand({ formData: formData, brandId: brandDetails?._id })
        );
      })();
    },
  });

  const handleImageUpload = (image: File | Image | null) => {
    setFieldValue("file", image);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <form onSubmit={handleSubmit}>
        <div className="lg:grid grid-cols-2 flex flex-col gap-6 my-4 py-8 lg:mx-8 mx-4">
          {/* Category Logo */}
          <div className="col-span-2">
            <label className="block text-base font-semibold mb-2">
              Brand Logo <span className="text-red-500">*</span>
            </label>

            <ImageUpload onUpload={handleImageUpload} />

            {errors.file && touched.file && (
              <p className="text-red-500 text-sm my-2">{errors.file}</p>
            )}
          </div>

          {/* Product Name */}
          <div className="col-span-1">
            <label className="block text-base font-semibold mb-2">
              Brand Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Untitled brand"
              className={CN(
                "block w-full p-3 border rounded-md",
                errors.name && touched.name
                  ? "border-red-500"
                  : "border-primary1"
              )}
            />
            {errors.name && touched.name && (
              <p className="text-red-500 text-sm my-2">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div className="col-span-1">
            <div className="flex flex-col gap-1 mb-2">
              <label className="block text-base font-semibold">website</label>
            </div>
            <input
              type="text"
              name="website"
              value={values.website}
              onChange={handleChange}
              onBlur={handleBlur}
              className={CN(
                "block w-full p-3 border rounded-md",
                errors.website && touched.website
                  ? "border-red-500"
                  : "border-primary1"
              )}
            />
            {errors.website && touched.website && (
              <p className="text-red-500 text-sm my-2">{errors.website}</p>
            )}
          </div>

           {/* country Name */}
                    <div className="col-span-1">
                      <label className="block text-base font-semibold mb-2">
                      Country
                      </label>
                      <input
                        type="text"
                        name="country"
                        value={values.country}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Brand country"
                        className={CN(
                          "block w-full p-3 border rounded-md",
                          errors.country && touched.country
                            ? "border-red-500"
                            : "border-primary1"
                        )}
                      />
                      {errors.country && touched.country && (
                        <p className="text-red-500 text-sm my-2">{errors.country}</p>
                      )}
                    </div>
          
                    {/* contact */}
                    <div className="col-span-1">
                      <div className="flex flex-col gap-1 mb-2">
                        <label className="block text-base font-semibold">
                        Contact
                        </label>
                      </div>
                      <input
                        type="text"
                        name="contact"
                        value={values.contact}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Brand contact"
                        className={CN(
                          "block w-full p-3 border rounded-md",
                          errors.contact && touched.contact
                            ? "border-red-500"
                            : "border-primary1"
                        )}
                      />
                      {errors.contact && touched.contact && (
                        <p className="text-red-500 text-sm my-2">{errors.contact}</p>
                      )}
                    </div>
        </div>

        {/* Buttons */}
        <div
          className={`flex justify-end flex-row gap-4 mt-5 py-4 w-full sticky bottom-0 bg-primary2 lg:px-8 px-4`}
        >
          <div className="col-span-2 flex justify-end gap-5">
            <Button onClick={() => navigate("/brands")} apparence="gray">
              Cancel
            </Button>

            <Button
              isLoading={isLoading.brandEdit}
              view="default"
              type="submit"
              disabled={!dirty}
            >
              <div className="flex gap-1">
                {isLoading.brandEdit && (
                  <Oval
                    height={18}
                    width={18}
                    color="#ffff"
                    wrapperStyle={{}}
                    wrapperClass="max-sm:mt-[3.5px] mt-[5.2px] max-lg:mt-[5.2px]"
                    visible={true}
                    ariaLabel="oval-loading"
                    secondaryColor="gray-primary"
                    strokeWidth={6}
                    strokeWidthSecondary={6}
                  />
                )}
                <div className="font-normal text-base">Save</div>
              </div>
            </Button>
          </div>
        </div>
      </form>
    </motion.div>
  );
};

export default EditBrand;
